import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowUpRight } from 'lucide-react';
import { PROJECTS } from '../../../data/portfolio';

gsap.registerPlugin(ScrollTrigger, useGSAP);

function Phone({ p }) {
  return (
    <div className="hv3-phone">
      <div
        className={`hv3-phone-screen ${p.screenTop ? 'has-inset' : ''}`}
        style={p.screenTop ? { '--screen-top': p.screenTop } : undefined}
      >
        <img src={p.screen} alt={`Tela do app ${p.name}`} width="600" height="1300" loading="lazy" decoding="async" />
      </div>
      <span className="hv3-phone-island" aria-hidden />
    </div>
  );
}

/**
 * Projetos numa trilha horizontal: o scroll vertical move a trilha (pin + scrub),
 * e dentro de cada painel o celular e o nome andam em velocidades diferentes.
 * No celular e com reduced-motion, os painéis empilham na vertical.
 */
function Work() {
  const root = useRef(null);

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();

      mm.add('(min-width: 900px) and (prefers-reduced-motion: no-preference)', () => {
        const section = root.current;
        const track = q('.hv3-track')[0];
        section.classList.add('is-horizontal');

        const distance = () => track.scrollWidth - window.innerWidth;
        const pan = gsap.to(track, {
          x: () => -distance(),
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 0.8,
            invalidateOnRefresh: true,
          },
        });

        gsap.to(q('.hv3-work-bar-fill'), {
          scaleX: 1,
          ease: 'none',
          scrollTrigger: { trigger: section, start: 'top top', end: () => `+=${distance()}`, scrub: true },
        });

        // Só os painéis de projeto têm celular, nome e brilho para o parallax.
        q('.hv3-panel[aria-label]').forEach((panel) => {
          const st = { trigger: panel, containerAnimation: pan, start: 'left right', end: 'right left', scrub: true };
          gsap.fromTo(panel.querySelector('.hv3-phone'), { xPercent: 28, rotate: 6 }, { xPercent: -28, rotate: -6, ease: 'none', scrollTrigger: st });
          gsap.fromTo(panel.querySelector('.hv3-panel-name'), { xPercent: -8 }, { xPercent: 8, ease: 'none', scrollTrigger: st });
          gsap.fromTo(panel.querySelector('.hv3-panel-glow'), { xPercent: 20, scale: 0.8 }, { xPercent: -20, scale: 1.1, ease: 'none', scrollTrigger: st });
        });

        return () => section.classList.remove('is-horizontal');
      });

      // Celular: carrossel de arrastar com encaixe. O celular de cada card
      // inclina conforme o card se afasta do centro, e a barra mostra o progresso.
      mm.add('(max-width: 899px)', () => {
        const rail = q('.hv3-rail')[0];
        const fill = q('.hv3-work-bar-fill')[0];
        const cards = q('.hv3-rail .hv3-panel[aria-label]');
        const phones = cards.map((c) => c.querySelector('.hv3-phone'));
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let raf = 0;
        const update = () => {
          raf = 0;
          const max = rail.scrollWidth - rail.clientWidth;
          gsap.set(fill, { scaleX: max > 0 ? rail.scrollLeft / max : 0 });
          if (reduced) return;
          const mid = rail.getBoundingClientRect().left + rail.clientWidth / 2;
          cards.forEach((c, i) => {
            const r = c.getBoundingClientRect();
            const d = gsap.utils.clamp(-1, 1, (r.left + r.width / 2 - mid) / rail.clientWidth);
            gsap.set(phones[i], { rotate: d * -9, xPercent: d * -18, yPercent: Math.abs(d) * 8 });
          });
        };
        const onScroll = () => {
          if (!raf) raf = requestAnimationFrame(update);
        };
        rail.addEventListener('scroll', onScroll, { passive: true });
        update();
        if (!reduced) {
          gsap.from(cards, {
            x: 60,
            autoAlpha: 0,
            duration: 1.1,
            ease: 'expo.out',
            stagger: 0.08,
            scrollTrigger: { trigger: rail, start: 'top 85%' },
          });
        }
        return () => {
          rail.removeEventListener('scroll', onScroll);
          cancelAnimationFrame(raf);
          gsap.set(phones, { clearProps: 'transform' });
        };
      });

      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <section className="hv3-work" id="trabalho" ref={root} aria-labelledby="trabalho-title">
      <div className="hv3-track">
        <div className="hv3-panel hv3-panel--intro">
          <h2 id="trabalho-title" className="hv3-work-title">
            Produtos reais.
            <br />
            <span className="hv3-green">No ar agora.</span>
          </h2>
          <p className="hv3-work-sub">
            Não mostramos o que poderíamos fazer. Mostramos o que já está nas mãos de usuários.
          </p>
        </div>

        <div className="hv3-rail">
        {PROJECTS.map((p) => (
          <article className="hv3-panel" key={p.slug} style={{ '--accent': p.accent }} aria-label={p.name}>
            <div className="hv3-panel-glow" aria-hidden />
            <div className="hv3-panel-copy">
              <h3 className="hv3-panel-name">{p.name}</h3>
              <p className="hv3-panel-tagline">{p.tagline}</p>
              <p className="hv3-panel-desc">{p.desc}</p>
              <ul className="hv3-tags" aria-label="Plataformas">
                {p.platforms.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
              <Link to={p.path} className="hv3-link">
                Ver projeto <ArrowUpRight size={18} strokeWidth={1.75} aria-hidden />
              </Link>
            </div>
            <div className="hv3-panel-media">
              <Phone p={p} />
            </div>
          </article>
        ))}

        <div className="hv3-panel hv3-panel--end">
          <Link to="/nosso-trabalho" className="hv3-endlink">
            <span>Ver todos os projetos</span>
            <ArrowUpRight size={40} strokeWidth={1.25} aria-hidden />
          </Link>
        </div>
        </div>
      </div>

      <div className="hv3-work-bar" aria-hidden>
        <span className="hv3-work-bar-fill" />
      </div>
    </section>
  );
}

export default Work;
